import { Link, useLocation } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Users,
  HeartHandshake,
  GraduationCap,
  FolderTree,
  Package,
  QrCode,
  Layers,
  Receipt,
  KanbanSquare,
  Truck,
  ScrollText,
  Webhook,
  KeyRound,
  Stethoscope,
  Bell,
  Sun,
  Moon,
  LogOut,
  Ticket,
  Tags,
  UserPlus,
} from "lucide-react";
import type { PropsWithChildren } from "react";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const NAV: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/users", label: "Users", icon: Users },
  { to: "/charities", label: "Charities", icon: HeartHandshake },
  { to: "/schools", label: "Schools", icon: GraduationCap },
  { to: "/categories", label: "Categories", icon: FolderTree },
  { to: "/items", label: "Items", icon: Package },
  { to: "/tags", label: "Tags", icon: QrCode },
  { to: "/tag-batches", label: "Tag batches", icon: Layers },
  { to: "/tag-orders", label: "Tag orders", icon: Receipt },
  { to: "/catalog", label: "Catalogue", icon: Tags },
  { to: "/promo-codes", label: "Promo codes", icon: Ticket },
  { to: "/referrals", label: "Referrals", icon: UserPlus },
  { to: "/claims", label: "Claims", icon: KanbanSquare },
  { to: "/returns", label: "Returns", icon: KanbanSquare },
  { to: "/postal", label: "Postal", icon: Truck },
  { to: "/audit", label: "Audit log", icon: ScrollText },
  { to: "/webhooks", label: "Webhooks", icon: Webhook },
  { to: "/api-keys", label: "API keys", icon: KeyRound },
  { to: "/launch-subscribers", label: "Subscribers", icon: Bell },
  { to: "/diagnostics", label: "Diagnostics", icon: Stethoscope },
];

export function AppShell({ children }: PropsWithChildren) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [dark, setDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add("dark");
    else root.classList.remove("dark");
  }, [dark]);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background text-foreground">
      <aside className="flex w-56 shrink-0 flex-col border-r bg-card">
        <div className="flex h-14 items-center px-4 font-semibold tracking-tight">
          PinStaQ Admin
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-2">
          {NAV.map((item) => {
            const active = location.pathname.startsWith(item.to);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  "mb-0.5 flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="border-t p-3 text-xs text-muted-foreground">
          {user?.email}
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 items-center justify-end gap-2 border-b bg-card px-4">
          <Button variant="ghost" size="icon" onClick={() => setDark((d) => !d)} aria-label="Toggle theme">
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => void signOut()} aria-label="Sign out">
            <LogOut className="h-4 w-4" />
          </Button>
        </header>
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}
